import { CoreEnums } from '@/utils/domain/constants';

const SOCKET = CoreEnums.SocketMessageType || {};
const TASK = CoreEnums.InternalTaskStatus || {};
const BULK = CoreEnums.BulkRequestTaskStatus || {};

const text = (value) => (typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '');

const truncate = (value, length = 180) => {
  const clean = text(value);
  if (clean.length <= length) return clean;
  return `${clean.slice(0, length - 1).trim()}…`;
};

// Keep an open ** run closed so a cut preview still parses as bold.
const truncateMarkdown = (value, length = 220) => {
  const clean = text(value);
  if (clean.length <= length) return clean;
  let cut = clean.slice(0, length);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > length * 0.6) cut = cut.slice(0, lastSpace);
  if ((cut.match(/\*\*/g) || []).length % 2 === 1) cut += '**';
  return `${cut.trim()}…`;
};

const stripHtml = (value) => truncate(String(value || '').replace(/<[^>]+>/g, ' '));

const uniqueJoin = (parts) => {
  const seen = new Set();
  return parts
    .map(text)
    .filter((part) => {
      if (!part || seen.has(part)) return false;
      seen.add(part);
      return true;
    })
    .join(' · ');
};

export const resolveSocketType = (rawType) => {
  if (typeof rawType === 'number') return rawType;
  const normalized = typeof rawType === 'string' ? rawType.trim().toLowerCase() : null;

  switch (normalized) {
    case 'entity':
      return SOCKET.Entity;
    case 'list':
      return SOCKET.List;
    case 'process':
    case 'process-instance':
    case 'processinstance':
      return SOCKET.ProcessInstance;
    case 'chat':
      return SOCKET.Chat;
    case 'bulk-request':
    case 'bulkrequest':
      return SOCKET.BulkRequest;
    case 'configuration-entity':
    case 'configurationentity':
      return SOCKET.ConfigurationEntity;
    case 'report':
      return SOCKET.Report;
    case 'document-export':
    case 'documentexport':
    case 'document':
      return SOCKET.DocumentExport;
    case 'all':
    case 'system':
      return SOCKET.All;
    default:
      return SOCKET.All;
  }
};

const CHANNEL_NAMES = new Set([
  'process-instance',
  'chat',
  'list',
  'entity',
  'bulk-request',
  'configuration-entity',
  'report',
  'document-export',
]);

const innerPayload = (notification) => {
  const data = notification?.data && typeof notification.data === 'object' ? notification.data : {};
  const type = typeof data.type === 'string' ? data.type.toLowerCase() : '';
  if (data.data && typeof data.data === 'object' && CHANNEL_NAMES.has(type)) {
    return data.data;
  }
  return data;
};

const STATUS_BY_NAME = {
  pending: TASK.Pending,
  processing: TASK.Processing,
  processed: TASK.Processed,
  error: TASK.Error,
  skipped: TASK.Skipped,
  cancelled: TASK.Cancelled,
  canceled: TASK.Cancelled,
  failed: TASK.Failed,
  paused: TASK.Paused,
};

const normalizeTaskStatus = (status) => {
  if (typeof status === 'string') return STATUS_BY_NAME[status.toLowerCase()] ?? null;
  return status;
};

const internalStatus = (status, formatMessage) => {
  const normalized = normalizeTaskStatus(status);
  const map = {
    [TASK.Pending]: ['notifications.status.queued', 'info'],
    [TASK.Processing]: ['notifications.status.inProgress', 'pending'],
    [TASK.Processed]: ['notifications.status.completed', 'success'],
    [TASK.Error]: ['notifications.statusText.error', 'error'],
    [TASK.Skipped]: ['notifications.status.skipped', 'default'],
    [TASK.Cancelled]: ['notifications.status.cancelled', 'default'],
    [TASK.Failed]: ['notifications.status.failed', 'error'],
    [TASK.Paused]: ['notifications.status.paused', 'default'],
  };
  const hit = map[normalized];
  if (!hit) return null;
  return { text: formatMessage({ id: hit[0] }), tone: hit[1] };
};

const bulkStatus = (status, formatMessage) => {
  const map = {
    [BULK.Pending]: ['notifications.status.queued', 'info'],
    [BULK.Processing]: ['notifications.status.inProgress', 'pending'],
    [BULK.Processed]: ['notifications.status.completed', 'success'],
    [BULK.Error]: ['notifications.status.failed', 'error'],
    [BULK.ProcessingTaskItems]: ['notifications.status.processingItems', 'pending'],
    [BULK.Canceled]: ['notifications.status.cancelled', 'default'],
  };
  const hit = map[status];
  if (!hit) return null;
  return { text: formatMessage({ id: hit[0] }), tone: hit[1] };
};

const operationLabel = (socketType, operationType, localizedOperation, formatMessage) => {
  if (socketType === SOCKET.ConfigurationEntity && text(localizedOperation)) {
    return text(localizedOperation);
  }

  if (socketType === SOCKET.Report) {
    if (
      operationType === CoreEnums.ReportInternalTaskOperationType?.RefreshDataset ||
      operationType === 2
    ) {
      return formatMessage({ id: 'notifications.operationType.refreshDataset' });
    }
    return formatMessage({ id: 'notifications.operationType.export' });
  }

  if (
    socketType === SOCKET.Entity &&
    operationType === CoreEnums.DynamicEntityInternalTaskOperationType?.Purge
  ) {
    return formatMessage({ id: 'notifications.operationType.purge' });
  }

  if (
    operationType === CoreEnums.DynamicEntityInternalTaskOperationType?.Import ||
    operationType === CoreEnums.ConfigurationEntityInternalTaskOperationType?.Import
  ) {
    return formatMessage({ id: 'notifications.operationType.import' });
  }

  return formatMessage({ id: 'notifications.operationType.export' });
};

const isExport = (socketType, operationType) => {
  if (socketType === SOCKET.DocumentExport) return true;
  if (socketType === SOCKET.List) {
    return (
      operationType == null ||
      operationType === CoreEnums.CustomListInternalTaskOperationType?.Export ||
      operationType === 1
    );
  }
  if (socketType === SOCKET.Report) {
    return (
      operationType !== CoreEnums.ReportInternalTaskOperationType?.RefreshDataset &&
      operationType !== 2
    );
  }
  if (socketType === SOCKET.Entity) {
    return (
      operationType === CoreEnums.DynamicEntityInternalTaskOperationType?.Export ||
      operationType === 1 ||
      operationType === 'export'
    );
  }
  if (socketType === SOCKET.ConfigurationEntity) {
    return (
      operationType === CoreEnums.ConfigurationEntityInternalTaskOperationType?.Export ||
      operationType === 1 ||
      operationType === 'export'
    );
  }
  return false;
};

const isProcessed = (status) => {
  const normalized = normalizeTaskStatus(status);
  return normalized === TASK.Processed;
};

const chatSubtitle = (chatMessageType, formatMessage) => {
  const kind = String(chatMessageType || '').toLowerCase();
  if (kind === 'whatsapp-message') return formatMessage({ id: 'notifications.chat.kind.whatsapp' });
  if (kind === 'email-message') return formatMessage({ id: 'notifications.chat.kind.email' });
  return formatMessage({ id: 'notifications.chat.threadLabel' });
};

const baseView = (overrides) => ({
  categoryKey: 'system',
  title: '',
  subtitle: '',
  message: '',
  status: null,
  importanceHigh: false,
  file: null,
  cta: null,
  markdown: false,
  ...overrides,
});

/**
 * Fields worth showing on a bell card. Company and environment stay off the card.
 * Schedule and project-orchestration events are not bell rows.
 */
export const describeNotification = (notification, formatMessage) => {
  const socketType = resolveSocketType(notification?.type);
  const payload = innerPayload(notification);
  const context =
    payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
      ? payload.data
      : {};
  const observations = truncate(payload?.observations || context?.observations);

  if (socketType === SOCKET.Chat) {
    const sender =
      text(payload.userName) || formatMessage({ id: 'notifications.chat.senderFallback' });
    const preview =
      truncateMarkdown(payload.messagePreview) ||
      truncateMarkdown(notification?.message) ||
      formatMessage({ id: 'notifications.chat.previewFallback' });
    const threadId = payload.threadId || notification?.chatThreadId;
    return baseView({
      categoryKey: 'chat',
      title: sender,
      subtitle: chatSubtitle(payload.chatMessageType, formatMessage),
      message: preview === sender ? '' : preview,
      markdown: true,
      cta: threadId
        ? { kind: 'chat', label: formatMessage({ id: 'notifications.chat.openThread' }) }
        : null,
    });
  }

  if (socketType === SOCKET.ProcessInstance) {
    const eventType = String(payload.type || notification?.data?.type || '').toLowerCase();
    const processInstanceId =
      notification?.processInstanceId || payload.processInstanceId || context.processInstanceId;
    const requestName = text(context.processInstanceName);
    const processName = text(context.processName);

    if (eventType && eventType !== 'new-task-assigned' && eventType !== 'new-comment') {
      return baseView({
        categoryKey: 'process',
        title: requestName || formatMessage({ id: 'notifications.processEvents.generic.label' }),
        subtitle: processName,
        cta: processInstanceId
          ? {
              kind: 'process',
              label: formatMessage({ id: 'notifications.processEvents.generic.cta' }),
            }
          : null,
      });
    }

    if (eventType === 'new-comment') {
      const comment = stripHtml(context.commentData?.commentText);
      const author = text(context.commentCreator?.resourceDisplay);
      return baseView({
        categoryKey: 'comment',
        title: requestName || formatMessage({ id: 'notifications.processEvents.newComment.title' }),
        subtitle: author,
        message: comment || formatMessage({ id: 'notifications.processEvents.newComment.body' }),
        cta: processInstanceId
          ? {
              kind: 'process',
              label: formatMessage({ id: 'notifications.processEvents.newComment.cta' }),
            }
          : null,
      });
    }

    const taskTitle = text(context.taskTitle);
    const title =
      taskTitle ||
      requestName ||
      formatMessage({ id: 'notifications.processEvents.newTaskAssigned.title' });
    const requestLabel = title === requestName ? '' : requestName;
    const processLabel =
      requestLabel && processName && requestLabel.toLowerCase().includes(processName.toLowerCase())
        ? ''
        : processName;
    return baseView({
      categoryKey: 'task',
      title,
      subtitle: uniqueJoin([processLabel, requestLabel]),
      importanceHigh: /^(high|urgent)$/i.test(text(context.taskImportance)),
      cta: processInstanceId
        ? {
            kind: 'process',
            label: formatMessage({ id: 'notifications.processEvents.newTaskAssigned.cta' }),
          }
        : null,
    });
  }

  if (socketType === SOCKET.BulkRequest) {
    const number = text(payload.bulkRequestNumber);
    const processName = text(payload.processName);
    const bulkRequestId = payload.bulkRequestId || notification?.bulkRequestId;
    return baseView({
      categoryKey: 'bulk',
      title: number || processName || formatMessage({ id: 'notifications.generic.title' }),
      subtitle: number ? processName : '',
      message: observations,
      status: bulkStatus(payload.status, formatMessage),
      cta: bulkRequestId
        ? { kind: 'bulk', label: formatMessage({ id: 'notifications.bulk.open' }) }
        : null,
    });
  }

  if (
    socketType === SOCKET.List ||
    socketType === SOCKET.Entity ||
    socketType === SOCKET.ConfigurationEntity ||
    socketType === SOCKET.Report ||
    socketType === SOCKET.DocumentExport
  ) {
    const categoryKey = {
      [SOCKET.List]: 'list',
      [SOCKET.Entity]: 'entity',
      [SOCKET.ConfigurationEntity]: 'configuration',
      [SOCKET.Report]: 'report',
      [SOCKET.DocumentExport]: 'document',
    }[socketType];

    const subject =
      text(payload.listName) ||
      text(payload.entityName) ||
      text(payload.entity) ||
      text(payload.reportName) ||
      text(payload.taskNumber) ||
      text(notification?.title);

    const file =
      isExport(socketType, payload.operationType) &&
      isProcessed(payload.taskStatus) &&
      payload.taskOutput
        ? payload.taskOutput
        : null;

    return baseView({
      categoryKey,
      title: subject || formatMessage({ id: 'notifications.generic.title' }),
      subtitle: operationLabel(socketType, payload.operationType, payload.operation, formatMessage),
      message: observations,
      status: internalStatus(payload.taskStatus, formatMessage),
      file,
    });
  }

  const title = text(notification?.title) || formatMessage({ id: 'notifications.generic.title' });
  const message = truncate(notification?.message);
  return baseView({
    title,
    message: message === title ? '' : message,
  });
};
